import { useRef } from "react";
import { t } from "i18next";
import FormDropdown from "../../components/form-dropdown/FormDropdown";
import { Typography } from "@material-tailwind/react";
import ReplenishmentPartsManager from "../replenishments/components/ReplenishmentPartsManager";
import SimpleSelect from "../../components/data/simple-select/SimpleSelect";

/**
 * Formulaire d'édition simplifié d'un replenishment
 * Champs READONLY : technician (FROM), assignedTechnician (TO), van
 * Champs EDITABLE : statut, priorité, pièces, commentaires
 */
export default function ReplenishmentAssignmentForm({
  data,
  onChange,
  onSubmit,
  submitted,
  isEditing,
  disabled = false,
}: {
  data?: Partial<EasyTech.Replenishment>,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean,
  isEditing?: boolean,
  disabled?: boolean
}) {
  const changeRef = useRef(onChange);
  changeRef.current = onChange;

  return (
    <>
      {/* INFO SECTION - Label: Value sur une ligne */}
      {isEditing && (
        <div className="field full-drawer mb-6 space-y-3">
          {/* Created BY */}
          {data?.requestFrom && (
            <div className="flex items-center gap-2">
              <Typography variant="small" className="text-gray-600 font-semibold min-w-fit">
                {t("replenishment:fields.requestFrom")}:
              </Typography>
              <Typography variant="small" className="text-blue-gray-900">
                {data?.requestFrom?.firstName} {data?.requestFrom?.lastName}
              </Typography>
            </div>
          )}

          {/* Technician FOR */}
          <div className="flex items-center gap-2">
            <Typography variant="small" className="text-gray-600 font-semibold min-w-fit">
              Technician (for):
            </Typography>
            <Typography variant="small" className="text-blue-gray-900">
              {data?.technician?.firstName} {data?.technician?.lastName}
            </Typography>
          </div>

          {/* VAN */}
          <div className="flex items-center gap-2">
            <Typography variant="small" className="text-gray-600 font-semibold min-w-fit">
              {t("replenishment:fields.vanToRestock")}:
            </Typography>
            <Typography variant="small" className="text-blue-gray-900">
              {data?.van?.name || "-"}
            </Typography>
          </div>

          {/* Technician TO */}
          <div className="flex items-center gap-2">
            <Typography variant="small" className="text-gray-600 font-semibold min-w-fit">
              Technician (to):
            </Typography>
            <Typography variant="small" className="text-blue-gray-900">
              {data?.assignedTechnician?.firstName} {data?.assignedTechnician?.lastName}
            </Typography>
          </div>
        </div>
      )}

      {/* SECTION 2: PRIORITÉ (EDITABLE) */}
      {isEditing && (
        <FormDropdown label={t("replenishment:fields.priority")}>
          <div className="field full-drawer">
            <SimpleSelect
              value={data?.priority || "NORMAL"}
              isDisabled={disabled}
              options={[
                { value: "NORMAL", label: t("replenishment:priority.NORMAL") },
                { value: "URGENT", label: t("replenishment:priority.URGENT") },
              ]}
              onChange={(value) => onChange("priority", value || "NORMAL")}
            />
          </div>
        </FormDropdown>
      )}

      {/* SECTION 3: PIÈCES (EDITABLE) */}
      {isEditing && (
        <FormDropdown label={t("replenishment:fields.partsOrdered")}>
          <div className="field full-drawer">
            <ReplenishmentPartsManager
              parts={data?.parts || []}
              onChange={(parts) => onChange("parts", parts)}
              disabled={disabled}
            />
          </div>
        </FormDropdown>
      )}

      {/* SECTION 7: COMMENTAIRES (EDITABLE) */}
      {isEditing && (
        <FormDropdown label={t("common:comments")}>
          <div className="field full-drawer">
            <textarea
              value={data?.comments || ""}
              onChange={(e) => onChange("comments", e.target.value)}
              placeholder={t("common:comments")}
              maxLength={1000}
              className="form-field w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={4}
              disabled={disabled}
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">
                {t("workorder:infos.notes.help")}
              </span>
              <span className={`text-xs ${(data?.comments?.length || 0) > 1000 ? "text-red-500 font-semibold" : "text-gray-500"}`}>
                {(data?.comments?.length || 0)}/1000
              </span>
            </div>
          </div>
        </FormDropdown>
      )}
    </>
  );
}
